/* store.ts */

/* Redux-Toolkit */
import { configureStore } from "@reduxjs/toolkit";

/* Redux Persist */
import storageModule from "redux-persist/lib/storage";
import {
  persistReducer,
  persistStore,
} from "redux-persist";

const storage =
  (storageModule as { default?: Storage }).default ??
  (storageModule as unknown as Storage);

/* Reducers */
import toastMessageReducer from "src/redux/features/toast-message/toastMessageSlice";
import scrollReducer from "src/redux/features/scrolling/scrollSlice";
import loadingScreenReducer from "src/redux/features/loading-screen/loadingScreenSlice";
import modalReducer from "src/redux/features/charts/modalSlice";
import providerReducer from "src/redux/features/auth/providerSlice";
import authReducer from "src/redux/features/auth/authSlice";
import secondaryMenuReducer from "src/redux/features/menu/secondary-menu/secondaryMenuSlice";
import pageTitleReducer from "src/redux/features/page-title/pageTitleSlice";
import insightsFilterReducer from "src/redux/features/insights-filter/insightsFilterSlice";
import insightsFilterOptionsReducer from "src/redux/features/insights-filter/insightsFilterOptionsSlice";
import survivalAnalysisOptionsReducer from "src/redux/features/insights-options/survival-analysis/survivalAnalysisOptionsSlice";
import sankeDiagramOptionsReducer from "src/redux/features/insights-options/treatment-patterns/sankey-diagram/sankeyDiagramOptionsSlice";
import descriptiveStatisticsOptionsReducer from "src/redux/features/insights-options/descriptive-statistics/descriptiveStatisticsOptionsSlice";
import descriptiveStatisticsReducer from "src/redux/features/charts/descriptiveStatisticsSlice";
import survivalAnalysisReducer from "src/redux/features/charts/survivalAnalysisSlice";
import treatmentPatternsReducer from "src/redux/features/charts/treatmentPatternsSlice";
import featureTypesReducer from "src/redux/features/custom-cohorts/featureTypesSlice";
import customCohortsReducer from "src/redux/features/custom-cohorts/customCohortsSlice";
import searchTermReducer from "src/redux/features/search-term/searchTermSlice";
import testDataReducer from "src/redux/features/test-data/testDataSlice";
import settingsReducer from "src/redux/features/settings/settingsSlice";
import agentChatReducer from "src/redux/features/agent-chat/agentChatSlice";

/* Config */
const authConfig = {
  key: "auth",
  storage,
  whitelist: ["data"],
};

/**
 * Configuration for custom cohorts.
 *
 * 'whitelist' is used to persist only parts of the slice.
 * The state to persist is 'customCohortsByDisease'.
 */
const customCohortsConfig = {
  key: "customCohorts",
  storage,
  whitelist: ["customCohortsByDisease"],
};

const testDataConfig = {
  key: "testData",
  storage,
};

const settingsConfig = {
  key: "settings",
  storage,
};

/**
 * Persisted reducers.
 *
 */
const persistendAuthReducer = persistReducer(
  authConfig,
  authReducer,
);

const persistendCustomCohortsReducer = persistReducer(
  customCohortsConfig,
  customCohortsReducer,
);

const persistedTestDataReducer = persistReducer(
  testDataConfig,
  testDataReducer,
);

const persistedSettingsReducer = persistReducer(
  settingsConfig,
  settingsReducer,
);

export const store = configureStore({
  reducer: {
    toastMessage: toastMessageReducer,
    scroll: scrollReducer,
    loadingScreen: loadingScreenReducer,
    modal: modalReducer,
    provider: providerReducer,
    auth: persistendAuthReducer,
    secondaryMenu: secondaryMenuReducer,
    pageTitle: pageTitleReducer,
    insightsFilter: insightsFilterReducer,
    insightsFilterOptions: insightsFilterOptionsReducer,
    survivalAnalysisOptions: survivalAnalysisOptionsReducer,
    sankeyDiagramOptions: sankeDiagramOptionsReducer,
    descriptiveStatisticsOptions: descriptiveStatisticsOptionsReducer,
    descriptiveStatistics: descriptiveStatisticsReducer,
    survivalAnalysis: survivalAnalysisReducer,
    treatmentPatterns: treatmentPatternsReducer,
    featureTypes: featureTypesReducer,
    customCohorts: persistendCustomCohortsReducer,
    searchTerm: searchTermReducer,
    testData: persistedTestDataReducer,
    settings: persistedSettingsReducer,
    agentChat: agentChatReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export const persistor = persistStore(store);
